/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../../platform/Platform';
import { ComponentManagerFn } from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { PivotColumnDefaultProps } from '../PivotTableStringColumn/PivotTableStringColumnManager';
import { defaultValue } from './PivotTableDateColumn';

const PivotTableDateColumnManager: ComponentManagerFn<
  'PivotTableDateColumn'
> = (platform: Platform) => {
  return {
    ...baseComponentManager<'PivotTableDateColumn'>(
      platform,
      'PivotTableDateColumn',
    ),
    type: 'PivotTableDateColumn',
    name: 'Pivot Table Date Column',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      ...PivotColumnDefaultProps,
      viewAttribute: {
        ...PivotColumnDefaultProps.viewAttribute,
        getAttributeDataTypes: () => ['Date', 'DateTime'],
      },
      prettyDate: {
        label: 'Pretty Date',
        type: 'boolean',
        helpText:
          'Enable this property to display date values in a human readable format.',
      },
    },
    getDisplayText: (item) => item.props.viewAttribute,
    category: 'Pivot Table',
    icon: { iconName: 'calendar-edit' },
    description: `**Overview**<br/>
The Pivot Table Date Column is a specialized column component used within a Pivot Table to display temporal data. It is specifically designed to handle and format date and time values from your connected data source, ensuring they are sorted and displayed according to chronological standards.
Use this component specifically when the underlying data attribute is of type **Date** or **DateTime**.

**1. Data Configuration**<br/>
* **View Attribute:** You must bind this column to a specific field in your data store. The component automatically filters for attributes defined as **Date** or **DateTime** to ensure correct parsing and avoid rendering errors.

**2. Formatting**<br/>
* **Pretty Date:** A boolean property that, when enabled, transforms raw ISO timestamps into human-readable strings.
* **Display Logic:** Instead of showing a technical string like \`2026-01-05T14:30:00\`, it will render a friendlier format such as **"Jan 5, 2026"** or relative terms like **"3 hours ago"**, depending on your platform's localization and time-distance settings.

**3. Interactivity**<br/>
* **TriggerOnClick:** Unlike standard read-only columns, this component supports the **TriggerOnClick** child component. 
* **Action:** This allows you to make date cells actionable. For example, clicking a "Due Date" cell could trigger a popup to reschedule a task, open a calendar view, or navigate to a detailed timeline for that specific record.

**4. Best Use Cases**<br/>
* **Project Schedules:** Displaying start and end dates in a project management overview.
* **Audit Logs:** Showing precise timestamps for system events or user transaction records.
* **Financial Reporting:** Organizing records by "Transaction Date" or "Fiscal Period."
* **Task Management:** Visualizing upcoming deadlines or expiration dates in a centralized task dashboard.`,
  };
};

export default PivotTableDateColumnManager;
